import { useState, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Input } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import { useRole } from '../../auth/useRole';
import { useTenantAdminUsers, type AdminUser, type Membership, type Role } from '../../api/adminUsers';
import { useTenantAdminWarehouses } from '../../api/adminWarehouses';
import type { UserMembership, WarehouseRole } from '../../api/adminMemberships';
import { StatusBadge } from '../../components/StatusBadge';
import { EditAccordion, type EditAccordionTab } from '../../components/ui/EditAccordion';
import { NewUserDrawer } from '../../components/admin/NewUserDrawer';
import { EditMembershipsDrawer } from '../../components/admin/EditMembershipsDrawer';
import { UserWarehouseGrantsDrawer } from '../../components/admin/UserWarehouseGrantsDrawer';
import { ResetPasswordModal } from '../../components/admin/ResetPasswordModal';
import { UserStatusTab } from '../../components/admin/UserStatusTab';
import { PageHeading } from '../../layouts/PageHeading';
import { useFilterSidebar } from '../../layouts/FilterSidebarContext';
import { PageFilters, FilterSection } from '../../components/ui/PageFilters';

const ALL_ROLES: Role[] = ['tenant-admin', 'warehouse-admin', 'voice-supervisor', 'voice-operator'];
const ALL_STATUS = ['active', 'disabled'] as const;

// Filter the user's tenant memberships down to the warehouse-scoped grants the
// drawer manages (warehouse-admin + voice-supervisor with a warehouse_id).
function toWarehouseGrants(memberships: Membership[]): UserMembership[] {
  const grants: UserMembership[] = [];
  for (const m of memberships) {
    if ((m.role !== 'warehouse-admin' && m.role !== 'voice-supervisor') ||
        m.warehouse_id === null || m.warehouse_code === null || m.warehouse_name === null) continue;
    grants.push({
      id: m.id,
      warehouse_id: m.warehouse_id,
      warehouse_code: m.warehouse_code,
      warehouse_name: m.warehouse_name,
      role: m.role as WarehouseRole,
      is_default: m.is_default,
    });
  }
  return grants;
}

// null = nothing open · 'create' = new-user panel · number = editing that user id
type Open = null | 'create' | number;

export default function UsersPage() {
  const { t } = useTranslation('user');
  const { token, tenantCode } = useTenantContext();
  // Read-only when the caller has no write-capable role (only voice-supervisor).
  const { isAnyAdmin } = useRole();
  const readOnly = !isAnyAdmin;
  const { data, isLoading } = useTenantAdminUsers(token, tenantCode);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const { data: warehousesData } = useTenantAdminWarehouses(token, tenantCode);
  const warehouses = warehousesData?.data ?? [];

  const [open, setOpen] = useState<Open>(null);

  const filtered = useMemo(() => {
    const all = data?.data ?? [];
    return all.filter((u) => {
      if (search) {
        const s = search.toLowerCase();
        const email = (u.email ?? '').toLowerCase();
        const name = (u.display_name ?? '').toLowerCase();
        if (!email.includes(s) && !name.includes(s)) return false;
      }
      if (roleFilter.length > 0 && !u.memberships.some((m) => roleFilter.includes(m.role))) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(u.status)) return false;
      return true;
    });
  }, [data, search, roleFilter, statusFilter]);

  function toggle<T extends string>(arr: T[], v: T): T[] {
    return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
  }

  // SP-8a — sidebar filtri.
  const sidebar = useMemo(() => (
    <PageFilters>
      <FilterSection title={t('page.filter.search', { defaultValue: 'Cerca' })}>
        <Input
          type="search"
          placeholder={t('page.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </FilterSection>
      <FilterSection title={t('page.filter.role')}>
        <div className="d-flex flex-wrap gap-1">
          {ALL_ROLES.map((r) => (
            <button key={r} type="button"
              className={`btn btn-sm ${roleFilter.includes(r) ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setRoleFilter(toggle(roleFilter, r))}>
              {t(`roles.${r}`, { defaultValue: r })}
            </button>
          ))}
        </div>
      </FilterSection>
      <FilterSection title={t('page.filter.status')}>
        <div className="d-flex flex-wrap gap-1">
          {ALL_STATUS.map((s) => (
            <button key={s} type="button"
              className={`btn btn-sm ${statusFilter.includes(s) ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(toggle(statusFilter, s))}>
              {t(`status.${s}`, { defaultValue: s })}
            </button>
          ))}
        </div>
      </FilterSection>
    </PageFilters>
  ), [search, roleFilter, statusFilter, t]);
  useFilterSidebar(sidebar);

  // A user is the "last admin" when they hold a tenant-admin role and they are
  // the only *active* tenant-admin in the tenant — disabling them would leave
  // the tenant with no administrator.
  const activeAdminIds = useMemo(
    () => (data?.data ?? [])
      .filter((u) => u.status === 'active' && u.memberships.some((m) => m.role === 'tenant-admin'))
      .map((u) => u.id),
    [data],
  );
  function isLastTenantAdmin(u: AdminUser): boolean {
    return u.memberships.some((m) => m.role === 'tenant-admin')
      && activeAdminIds.length === 1 && activeAdminIds[0] === u.id;
  }

  /**
   * Il tab "Accessi magazzini" gestisce SOLO i ruoli warehouse-admin e
   * voice-supervisor. Su un utente che è unicamente operatore non ha nulla da
   * mostrare, e non è inerte: confermando il form gli si aggiunge un ruolo
   * amministrativo. Chi cerca "sposta l'operatore di magazzino" finirebbe lì e
   * lo promuoverebbe invece di spostarlo — il magazzino dell'operatore si
   * gestisce dal tab Ruoli.
   *
   * Un utente senza alcuna membership continua a vederlo: è da lì che gli si
   * assegnano gli accessi.
   */
  function isOperatorOnly(u: AdminUser): boolean {
    return u.memberships.length > 0
      && u.memberships.every((m) => m.role === 'voice-operator');
  }

  function userTabs(u: AdminUser): EditAccordionTab[] {
    return [
      { id: 'memberships', label: t('tabs.memberships'),
        content: <EditMembershipsDrawer inline user={u} isOpen onClose={() => setOpen(null)} /> },
      ...(isOperatorOnly(u) ? [] : [{ id: 'grants', label: t('tabs.warehouseGrants'),
        content: (
          <UserWarehouseGrantsDrawer
            inline isOpen onClose={() => setOpen(null)}
            user={{ id: u.id, email: u.email }}
            memberships={toWarehouseGrants(u.memberships)}
            warehouses={warehouses}
          />
        ) }]),
      { id: 'password', label: t('tabs.password'),
        content: <ResetPasswordModal inline user={u} onClose={() => setOpen(null)} /> },
      { id: 'status', label: t('tabs.status'),
        content: <UserStatusTab user={u} isLastTenantAdmin={isLastTenantAdmin(u)} onClose={() => setOpen(null)} /> },
    ];
  }

  const COLS = 4;

  return (
    <div>
      <PageHeading
        scope={t('page.heading')}
        readOnly={readOnly}
        actions={!readOnly ? (
          <Button color="primary" onClick={() => setOpen((o) => (o === 'create' ? null : 'create'))}>
            <i className="bi bi-plus-lg me-1" />{t('page.newUserButton')}
          </Button>
        ) : undefined}
      />

      {!readOnly && open === 'create' && (
        <EditAccordion
          isOpen onClose={() => setOpen(null)}
          icon="bi-person-plus" title={t('tabs.newUser')}
          tabs={[{ id: 'new', label: t('tabs.newUser'), content: <NewUserDrawer inline isOpen onClose={() => setOpen(null)} /> }]}
        />
      )}

      {isLoading ? (
        <p className="text-muted">{t('page.loading')}</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted">{t('page.empty')}</p>
      ) : (
        <div className="table-responsive">
        <Table className="table-hover align-middle table-striped mb-0">
          <thead>
            <tr><th>{t('table.email')}</th><th>{t('table.name')}</th><th>{t('table.status')}</th><th>{t('table.memberships')}</th></tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isRowOpen = open === u.id;
              return (
                <FragmentRow key={u.id}>
                  <tr
                    className={isRowOpen ? 'table-active' : undefined}
                    style={readOnly ? undefined : { cursor: 'pointer' }}
                    onClick={readOnly ? undefined : () => setOpen((o) => (o === u.id ? null : u.id))}
                    aria-expanded={!readOnly ? isRowOpen : undefined}
                  >
                    <td>{u.email ?? '—'}</td>
                    <td>{u.display_name ?? '—'}</td>
                    <td>{t(`status.${u.status}`, { defaultValue: u.status })}</td>
                    <td>
                      {u.memberships.map((m) => (
                        <StatusBadge key={m.id} tone="info" className="me-1">
                          {t(`roles.${m.role}`, { defaultValue: m.role })}{m.warehouse_code ? ` @ ${m.warehouse_code}` : ''}
                        </StatusBadge>
                      ))}
                    </td>
                  </tr>
                  {!readOnly && isRowOpen && (
                    <tr className="user-edit-row">
                      <td colSpan={COLS} className="p-0 border-0">
                        <EditAccordion
                          isOpen onClose={() => setOpen(null)}
                          icon="bi-person" code={u.email ?? undefined}
                          title={u.display_name ?? t('page.fallbackLabel', { id: u.id })}
                          sub={`${t('page.statusInline')}${t(`status.${u.status}`, { defaultValue: u.status })}`}
                          tabs={userTabs(u)}
                        />
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
          </tbody>
        </Table>
        </div>
      )}
    </div>
  );
}

function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
